/**
 * Customer list state machine - replaces useCustomerStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { Customer, CustomerFilters, CustomerStats } from '../types'

const defaultFilters: CustomerFilters = {
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc',
}

export interface CustomerListContext {
  customers: Customer[]
  selectedCustomer: Customer | null
  totalCount: number
  stats: CustomerStats | null
  filters: CustomerFilters
  isLoading: boolean
}

export type CustomerListEvent =
  | { type: 'SET_CUSTOMERS'; customers: Customer[]; total: number }
  | { type: 'SET_SELECTED_CUSTOMER'; customer: Customer | null }
  | { type: 'ADD_CUSTOMER'; customer: Customer }
  | { type: 'UPDATE_CUSTOMER'; id: string; updates: Partial<Customer> }
  | { type: 'REMOVE_CUSTOMER'; id: string }
  | { type: 'SET_STATS'; stats: CustomerStats }
  | { type: 'SET_FILTERS'; filters: Partial<CustomerFilters> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as CustomerListContext,
    events: {} as CustomerListEvent,
  },
})

export const customerListMachine = machineSetup.createMachine({
  id: 'customerList',
  context: {
    customers: [],
    selectedCustomer: null,
    totalCount: 0,
    stats: null,
    filters: defaultFilters,
    isLoading: false,
  },
  on: {
    SET_CUSTOMERS: {
      actions: assign({
        customers: ({ event }) => (event.type === 'SET_CUSTOMERS' ? event.customers : []),
        totalCount: ({ event }) => (event.type === 'SET_CUSTOMERS' ? event.total : 0),
      }),
    },
    SET_SELECTED_CUSTOMER: {
      actions: assign({
        selectedCustomer: ({ event }) =>
          event.type === 'SET_SELECTED_CUSTOMER' ? event.customer : null,
      }),
    },
    ADD_CUSTOMER: {
      actions: assign({
        customers: ({ context, event }) =>
          event.type === 'ADD_CUSTOMER'
            ? [event.customer, ...context.customers]
            : context.customers,
        totalCount: ({ context, event }) =>
          event.type === 'ADD_CUSTOMER' ? context.totalCount + 1 : context.totalCount,
      }),
    },
    UPDATE_CUSTOMER: {
      actions: assign({
        customers: ({ context, event }) => {
          if (event.type !== 'UPDATE_CUSTOMER') return context.customers
          return context.customers.map((c) => (c.id === event.id ? { ...c, ...event.updates } : c))
        },
        selectedCustomer: ({ context, event }) => {
          if (event.type !== 'UPDATE_CUSTOMER') return context.selectedCustomer
          if (context.selectedCustomer?.id !== event.id) return context.selectedCustomer
          return { ...context.selectedCustomer, ...event.updates }
        },
      }),
    },
    REMOVE_CUSTOMER: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REMOVE_CUSTOMER') return {}
        const existed = context.customers.some((c) => c.id === event.id)
        return {
          customers: context.customers.filter((c) => c.id !== event.id),
          totalCount: existed ? context.totalCount - 1 : context.totalCount,
          selectedCustomer:
            context.selectedCustomer?.id === event.id ? null : context.selectedCustomer,
        }
      }),
    },
    SET_STATS: {
      actions: assign({
        stats: ({ event }) => (event.type === 'SET_STATS' ? event.stats : null),
      }),
    },
    SET_FILTERS: {
      actions: assign({
        filters: ({ context, event }) => {
          if (event.type !== 'SET_FILTERS') return context.filters
          const next = { ...context.filters, ...event.filters }
          if (!('page' in event.filters)) next.page = 1
          return next
        },
      }),
    },
    RESET_FILTERS: {
      actions: assign({ filters: defaultFilters }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    CLEAR: {
      actions: assign({
        customers: [],
        selectedCustomer: null,
        totalCount: 0,
        stats: null,
        filters: defaultFilters,
        isLoading: false,
      }),
    },
  },
})
